import { Lightning, Colors, AppData, Router } from '@lightningjs/sdk';
import { translate } from 'support/translate';
import Button from 'components/common/buttons/Button';
import { HoverableComponent } from 'components/common/HoverableComponent';
import { constants } from '../../../../aliases';
import { getImageTextureObj } from 'support/generalUtils';
import ActionsList from 'components/common/ActionsList';
import { getFontFaceFromStyle } from 'support/textUtils';
import CheckmarkText from 'components/common/CheckmarkText';
import LoginQrCode from '../common/LoginQrCode';
import OfflineErrorToast from 'components/common/toasts/OfflineErrorToast';
import { PageId } from 'types/pageId';
import { CtvCarouselItem } from 'types/api/config';
import LoginLandingBillboardCarousel from 'components/pages/login/login-landing/LoginLandingBillboardCarousel';
import LoginPage from '../LoginPage';
import { LoginFlow } from '../layout/AbstractLoginModal';
import TextLabel from 'components/common/TextLabel';
import {
  reportGetStarted,
  reportLoginSkipped,
} from 'services/analytics/reportingServicePage';

const CONTENT_OFFSET_X = 80;
const CONTENT_OFFSET_Y = 60;
const CONTENT_HEIGHT = 960;
const CONTENT_SPACING = 32;

const LOGO_ICON = 'static/images/brand-logo-white.svg';
const LOGO_TEXTURE = getImageTextureObj(LOGO_ICON, 91, 40);

const QR_CODE_SIZE = 174;

const HEADING_HEIGHT = 90;
const HEADING_FONT_SIZE = 56;
const SUBHEADING_HEIGHT = 38;
const SUBHEADING_FONT_SIZE = 24;

const DETAIL_ITEM_HEIGHT = 29;
const DETAIL_ITEM_FONT_SIZE = 18;
const DETAIL_ITEM_ICON_SIZE = 18;
const DETAIL_ITEM_SPACING = 12;
const DETAIL_ITEM_CONTENT_MARGIN = 12;

const BUTTON_WIDTH = 523;
const BUTTON_HEIGHT = 65;
const BUTTON_PADDING = 18;
const BUTTON_FONT_SIZE = 18;
const BUTTON_SPACING = 22;
const OFFLINE_ERROR_X = 1136;
const OFFLINE_ERROR_Y = 60;

export interface LoginLandingTemplateSpec
  extends Lightning.Component.TemplateSpec {
  Title: typeof TextLabel;
  Description: typeof TextLabel;
  BillboardCarousel: typeof HoverableComponent;
  OfflineErrorToast: typeof OfflineErrorToast;
  Content: {
    Logo: Lightning.Texture;
    QrCode: typeof LoginQrCode;
    Heading: typeof TextLabel;
    SubHeading: typeof TextLabel;
    DetailsList: object;
    ActionsList: typeof ActionsList;
  };
}

export default class LoginLanding
  extends HoverableComponent<LoginLandingTemplateSpec>
  implements
    Lightning.Component.ImplementTemplateSpec<LoginLandingTemplateSpec>
{
  private ctvCarouselItems?: CtvCarouselItem[];

  private _pageId = PageId.INITIAL_LANDING_PAGE;
  private _BillboardCarousel = this.getByRef('BillboardCarousel')!;
  private _OfflineErrorToast = this.getByRef('OfflineErrorToast')!;
  private _Content = this.getByRef('Content')!;
  private _Heading = this._Content.getByRef('Heading')!;
  private _SubHeading = this._Content.getByRef('SubHeading')!;
  private _Details = this._Content.getByRef('DetailsList')!;
  private _Actions = this._Content.getByRef('ActionsList')!;

  get announce() {
    const detailText = this._Details.children.map(
      child => (child as CheckmarkText).title,
    );

    return [
      this._Heading.text?.text,
      this._SubHeading.text?.text,
      ...detailText,
    ];
  }

  static override _template(): Lightning.Component.Template<LoginLandingTemplateSpec> {
    return {
      alpha: 0,
      rect: true,
      w: 1920,
      h: 1080,
      color: Colors('modalBackground').get(),
      zIndex: constants.ui.zIndex.modalLayer,
      BillboardCarousel: {
        type: HoverableComponent,
        visible: false,
        signals: {
          $onHover: '$onHover',
        },
      },
      OfflineErrorToast: {
        type: OfflineErrorToast,
        x: OFFLINE_ERROR_X,
        y: OFFLINE_ERROR_Y,
        alpha: constants.ui.invisible, //To have the image preload early
        zIndex: constants.ui.zIndex.spinnerLayer,
        signals: {
          $onHover: false,
        },
      },
      Content: {
        x: CONTENT_OFFSET_X,
        y: CONTENT_OFFSET_Y,
        h: CONTENT_HEIGHT,
        flex: {
          direction: 'column',
          justifyContent: 'flex-end',
        },
        Logo: {
          ...LOGO_TEXTURE,
          flexItem: false,
        },
        QrCode: {
          type: LoginQrCode,
          size: QR_CODE_SIZE,
          flexItem: { marginBottom: CONTENT_SPACING },
        },
        Heading: {
          type: TextLabel,
          text: {
            text: translate('account.upsell.heading'),
            verticalAlign: 'middle',
            fontSize: HEADING_FONT_SIZE,
            fontFace: getFontFaceFromStyle('bold'),
            lineHeight: HEADING_HEIGHT,
            textColor: Colors('labelText').get(),
          },
        },
        SubHeading: {
          type: TextLabel,
          text: {
            text: translate('account.upsell.subheading'),
            verticalAlign: 'middle',
            fontSize: SUBHEADING_FONT_SIZE,
            lineHeight: SUBHEADING_HEIGHT,
            textColor: Colors('labelText').get(),
          },
          flexItem: { marginBottom: CONTENT_SPACING },
        },
        DetailsList: {
          flex: {
            direction: 'column',
          },
          flexItem: { marginBottom: CONTENT_SPACING },
        },
        ActionsList: {
          flex: {
            direction: 'column',
          },
          type: ActionsList,
          spacing: BUTTON_SPACING,
          items: [],
          signals: {
            $signin: true,
            $skip: true,
          },
        },
      },
    };
  }

  override _enable() {
    this.ctvCarouselItems =
      AppData?.config?.login.landing_page.ctv_carousel.items;
    if (this.ctvCarouselItems?.length) {
      this.updateDetails();
      this.buildBillboard();
    }
  }

  override _onActivated() {
    this.updateHeader();
    this.updateButtons();

    this._resetOfflineToast();
  }

  private _onOffline() {
    this._OfflineErrorToast.patch({ alpha: 1 });
  }

  private _backOnline() {
    this._OfflineErrorToast.patch({ alpha: 0.01 }); //make sure image reloads while online
  }

  // TODO: This will needed to be moved/refactored to happen everywhere
  // except playback at a later date
  private _resetOfflineToast() {
    AppData!.offlineErrorService.addOfflineAction(
      'offline',
      this._onOffline.bind(this),
    );

    AppData!.offlineErrorService.resumeOnlineAction(
      'offline',
      this._backOnline.bind(this),
    );

    AppData!.offlineErrorService.isOffline()
      ? this._onOffline()
      : this._backOnline();
  }

  override _handleKey() {
    return true;
  }

  override _getFocused() {
    return this._Actions;
  }

  override _handleHover(target: any) {
    return true;
  }

  override _handleBack() {
    const loginRegistrationShownTimestamp =
      AppData!.storageService.loginRegistrationShownTimestamp.get();

    // Show exit modal if the login modal is shown on launch, not by app interaction
    if (!loginRegistrationShownTimestamp) {
      AppData?.modalService.open('ExitModal');
    } else {
      this.onLoginExit();
    }
  }

  hide() {
    this.patch({ alpha: 0 });
  }

  show() {
    this.patch({ alpha: 1 });
    this._resetOfflineToast();
  }

  private onLoginExit() {
    const activePage = Router.getActivePage() as LoginPage;
    activePage.onLoginExit();
  }

  private $signin() {
    reportGetStarted(this._pageId);

    AppData?.modalService.open('EmailInputModal');
  }

  private $skip() {
    reportLoginSkipped(this._pageId, LoginFlow.GET_STARTED);
    this.onLoginExit();
  }

  private buildBillboard() {
    if (!this.ctvCarouselItems?.length) return;

    const loginLandingItems: CtvCarouselItem[] = this.ctvCarouselItems.map(
      item => {
        return {
          title: item.title,
          subtitle: item.subtitle,
          image_url: item.image_url,
        };
      },
    );

    this._BillboardCarousel.children = [
      {
        type: LoginLandingBillboardCarousel,
        lane: { type: 'login_landing', items: loginLandingItems },
        signals: {
          $onHover: '$onHover',
        },
      },
    ];

    this._BillboardCarousel.visible = true;
  }

  private updateHeader() {
    if (!AppData?.config?.login.landing_page) return;

    const { header, subheader } = AppData.config.login.landing_page;
    this._Heading.patch({ text: { text: header } });
    this._SubHeading.patch({ text: { text: subheader } });
  }

  private updateDetails() {
    this._Details?.childList.clear();

    const labels = this.ctvCarouselItems?.map(item => item.subtitle);
    labels?.forEach((label, index) =>
      this._Details?.add({
        type: CheckmarkText,
        flexItem: {
          marginTop: index > 0 ? DETAIL_ITEM_SPACING : 0,
        },
        isSelected: true,
        minWidth: BUTTON_WIDTH,
        height: DETAIL_ITEM_HEIGHT,
        fontSize: DETAIL_ITEM_FONT_SIZE,
        iconSize: DETAIL_ITEM_ICON_SIZE,
        label,
        margin: DETAIL_ITEM_CONTENT_MARGIN,
      }),
    );
  }

  private updateButtons() {
    const buttonBase = {
      type: Button,
      minWidth: BUTTON_WIDTH,
      height: BUTTON_HEIGHT,
      padding: BUTTON_PADDING,
      fontSize: BUTTON_FONT_SIZE,
      signals: {
        $onHover: true,
        $onUnhover: true,
      },
      zIndex: constants.ui.zIndex.modalLayer + 1,
    };

    const buttons = [
      {
        ...buttonBase,
        label: translate('account.upsell.signupin'),
        action: '$signin',
        passSignals: { $signin: '$signin' },
      },
      {
        ...buttonBase,
        label: translate('account.upsell.startWatching'),
        action: '$skip',
        passSignals: { $skip: '$skip' },
      },
    ];

    this._Actions.items = buttons;
  }
}
